import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Zap, Play } from "lucide-react";
import { toast } from "sonner";

function Toggle({ tid, checked, onChange, label, hint }) {
  return (
    <label className="flex items-start gap-3 cursor-pointer select-none">
      <input type="checkbox" data-testid={tid} checked={!!checked} onChange={(e) => onChange(e.target.checked)} className="mt-0.5 h-4 w-4 accent-zinc-900" />
      <span>
        <span className="text-sm font-medium text-zinc-900">{label}</span>
        {hint ? <span className="block text-xs text-zinc-500">{hint}</span> : null}
      </span>
    </label>
  );
}

export default function GstAutomationPanel() {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => setD((await api.get("/gst-automation")).data);
  useEffect(() => { load(); }, []);

  const patch = async (upd) => {
    try {
      const { data } = await api.patch("/gst-automation", upd);
      setD((p) => ({ ...p, settings: data }));
    } catch (e) { toast.error("Update failed", { description: e.response?.data?.detail || e.message }); }
  };

  const runNow = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/gst-automation/run");
      const t = data.tasks?.created_count ?? 0, n = data.nudges?.sent_count ?? 0;
      toast.success("GST automation ran", { description: `${t} task(s) created · ${n} client nudge(s) sent` });
      load();
    } catch (e) { toast.error("Run failed", { description: e.response?.data?.detail || e.message }); }
    finally { setBusy(false); }
  };

  if (!d) return null;
  const s = d.settings;
  const last = d.last_run;

  return (
    <section className="bg-white border border-zinc-200 p-6" data-testid="gst-automation-section">
      <header className="flex flex-wrap items-center justify-between gap-3 pb-3 mb-4 border-b border-zinc-200">
        <div className="flex items-center gap-3">
          <Zap size={16} strokeWidth={1.75} />
          <h2 className="font-heading font-bold tracking-tight text-lg">Filing automation</h2>
          <span className="text-[10px] tracking-[0.14em] uppercase text-zinc-500 font-semibold">runs daily 09:00 IST</span>
        </div>
        <Button data-testid="gst-run-now-btn" onClick={runNow} disabled={busy} variant="outline" className="rounded-none border-zinc-300">
          <Play size={14} className="mr-1.5" /> {busy ? "Running…" : "Run now"}
        </Button>
      </header>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-3">
          <Toggle tid="gst-tasks-enabled" checked={s.tasks_enabled} onChange={(v) => patch({ tasks_enabled: v })}
            label="Auto-create filing tasks" hint="GSTR-1 / GSTR-3B, PMT-06 / quarterly GST, TDS quarterly returns and ITR / tax-audit deadlines for every tagged client, auto-assigned to the right specialist." />
          <div className="flex items-center gap-3 pl-7">
            <Label className="text-[11px] tracking-[0.14em] uppercase font-semibold whitespace-nowrap">Create</Label>
            <Input data-testid="gst-tasks-lead-days" type="number" min={1} max={30} value={s.tasks_lead_days} onChange={(e) => patch({ tasks_lead_days: Number(e.target.value) || 1 })} className="w-20 rounded-none" />
            <span className="text-xs text-zinc-600">days before due date</span>
          </div>
          <div className="flex items-center gap-3 pl-7">
            <Label className="text-[11px] tracking-[0.14em] uppercase font-semibold">Priority</Label>
            <select data-testid="gst-tasks-priority" value={s.tasks_priority} onChange={(e) => patch({ tasks_priority: e.target.value })} className="text-sm border border-zinc-300 px-2 py-1.5 bg-white">
              {["High", "Medium", "Low"].map(p => <option key={p}>{p}</option>)}
            </select>
          </div>
          <div className="pl-7 text-xs text-zinc-500" data-testid="gst-open-auto-tasks">Open auto-created tasks: <b className="text-zinc-900 tabular">{d.open_auto_tasks}</b></div>
        </div>

        <div className="space-y-3">
          <Toggle tid="gst-nudges-enabled" checked={s.nudges_enabled} onChange={(v) => patch({ nudges_enabled: v })}
            label="Client document nudges" hint="Ask GST / TDS / ITR clients for pending documents before each filing. Sent once per client per return." />
          <div className="flex items-center gap-3 pl-7">
            <Label className="text-[11px] tracking-[0.14em] uppercase font-semibold whitespace-nowrap">Send</Label>
            <Input data-testid="gst-nudges-lead-days" type="number" min={1} max={30} value={s.nudges_lead_days} onChange={(e) => patch({ nudges_lead_days: Number(e.target.value) || 1 })} className="w-20 rounded-none" />
            <span className="text-xs text-zinc-600">days before due date</span>
          </div>
          <div className="pl-7 space-y-2">
            <Toggle tid="gst-nudge-email" checked={s.nudge_email} onChange={(v) => patch({ nudge_email: v })} label="Email" />
            <Toggle tid="gst-nudge-whatsapp" checked={s.nudge_whatsapp} onChange={(v) => patch({ nudge_whatsapp: v })} label="WhatsApp"
              hint={d.whatsapp_ready ? "Twilio ready" : "Needs Twilio WhatsApp keys — skipped until configured"} />
          </div>
        </div>
      </div>

      <div className="mt-6 pt-4 border-t border-zinc-100 grid grid-cols-1 md:grid-cols-2 gap-6">
        <div data-testid="gst-last-run">
          <div className="text-[10px] tracking-[0.14em] uppercase text-zinc-500 font-semibold mb-1">Last run</div>
          {last ? (
            <div className="text-xs text-zinc-700 space-y-0.5">
              <div>{new Date(last.finished_at).toLocaleString("en-IN")} · <span className="uppercase tracking-wider text-[10px]">{last.trigger}</span></div>
              <div>Tasks created: <b className="tabular">{last.tasks?.created_count ?? 0}</b> · skipped (already exist): <b className="tabular">{last.tasks?.skipped_existing ?? 0}</b></div>
              <div>Nudges sent: <b className="tabular">{last.nudges?.sent_count ?? 0}</b> · skipped (already sent): <b className="tabular">{last.nudges?.skipped_already_sent ?? 0}</b></div>
              {(last.tasks?.errors?.length || last.nudges?.errors?.length) ? (
                <div className="text-rose-700">{(last.tasks?.errors || []).concat(last.nudges?.errors || []).map((e, i) => <div key={i}>{e.client} · {e.form}{e.channel ? ` · ${e.channel}` : ""}: {e.error}</div>)}</div>
              ) : null}
            </div>
          ) : <div className="text-xs text-zinc-500">Not run yet.</div>}
        </div>
        <div>
          <div className="text-[10px] tracking-[0.14em] uppercase text-zinc-500 font-semibold mb-1">Recent client nudges</div>
          {d.recent_nudges.length ? (
            <ul className="text-xs divide-y divide-zinc-100" data-testid="gst-recent-nudges">
              {d.recent_nudges.slice(0, 8).map(n => (
                <li key={n.key} className="py-1 flex items-center gap-2">
                  <span className={`w-1.5 h-1.5 rounded-full ${n.ok ? "bg-emerald-500" : "bg-rose-500"}`} />
                  <span className="font-medium truncate">{n.client_name}</span>
                  <span className="text-zinc-500 truncate">{n.form} · {n.channel} · {n.to}</span>
                  <span className="ml-auto text-zinc-400 tabular whitespace-nowrap">{n.sent_at?.slice(0, 10)}</span>
                </li>
              ))}
            </ul>
          ) : <div className="text-xs text-zinc-500">No nudges sent yet.</div>}
        </div>
      </div>
    </section>
  );
}
